import { Component, EventEmitter, Injector, Input, Output, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { AbstractComponent, TASK_OUTCOME_LABEL, TASK_TYPE_ICON, Task, TasksService, TaskStatusEnum, TaskTypeEnum, taskTypeLabelKey } from 'lib-trend-core';
import { takeUntil } from 'rxjs';
import { ConcludeActivityDialogComponent, ConcludeActivityDialogResult } from '../activity-dialogs/conclude-activity-dialog/conclude-activity-dialog.component';
import { PostponeActivityDialogComponent } from '../activity-dialogs/postpone-activity-dialog/postpone-activity-dialog.component';

/** The state a row shows, legible without colour: a key, a badge variant and an icon (RNF-08). */
export interface ActivityRowState {
  key: string;
  badge: 'is-ok' | 'is-warn' | 'is-err' | 'is-run' | 'is-neutral';
  icon: string;
}

/**
 * THE activity row and list of the workspace (SPEC-2026-10-crm-activities-cadence RF-36 to RF-43):
 * the Today groups, the week and month days, the calendar's day and the context panel's upcoming
 * list all render this — one row, one dots menu, one place where Concluir and Adiar live
 * (RF-17, RF-24). Material Design Icons throughout, as the listing rule's dots menu.
 *
 * The component opens the dialogs itself and emits `changed` afterwards; the views reload on it.
 */
@Component({
  selector: 'app-activity-list',
  templateUrl: './activity-list.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ActivityListComponent extends AbstractComponent {

  @Input() activities: Array<Task> = [];
  @Input() emptyKey = 'task.activity.empty';
  /** The deal's name beside the title; nothing when the deal is not visible to the user (RF-44). */
  @Input() showDeal = true;
  /** The responsible person's column — off in the personal views. */
  @Input() showResponsible = false;
  /** Rows select instead of opening: the Today view's context panel (RF-40). */
  @Input() selectable = false;
  @Input() selectedId?: string;
  /** `HH:mm` only (the Today groups), or the day too (ranges). */
  @Input() timedFormat = 'DD/MM HH:mm';
  @Input() dateFormat = 'DD/MM';

  @Output() changed = new EventEmitter<Task>();
  @Output() selected = new EventEmitter<Task>();

  constructor(
    injector: Injector,
    private readonly dialog: MatDialog,
    private readonly tasksService: TasksService,
  ) {
    super(injector);
  }

  typeLabel(task: Task): string {
    return taskTypeLabelKey(task?.type);
  }

  typeIcon(task: Task): string {
    return TASK_TYPE_ICON[task?.type as TaskTypeEnum] ?? 'mdi-checkbox-blank-circle-outline';
  }

  /** The task's own title — unless it is the API's fallback (the type key itself), which reads as the type label. */
  title(task: Task): string {
    const name = task?.name?.trim();
    return name && name !== task?.type ? name : this.t(taskTypeLabelKey(task?.type));
  }

  dealName(task: Task): string {
    return task?.card?.name ?? '';
  }

  idOfDeal(task: Task): string {
    return `${(task?.card as { _id?: unknown })?._id ?? ''}`;
  }

  isOpen(task: Task): boolean {
    return task?.status !== TaskStatusEnum.DONE;
  }

  /**
   * The activity's deal is outside the reader's visibility (E4 validation N2): concluding and
   * postponing need the deal, so the row says so and leaves the actions to a supervisor.
   */
  isLocked(task: Task): boolean {
    return task?.dealHidden === true;
  }

  canConclude(task: Task): boolean {
    return this.isOpen(task) && !this.isLocked(task);
  }

  canPostpone(task: Task): boolean {
    return this.canConclude(task) && !!task?.deadlineAt;
  }

  momentFormat(task: Task): string {
    return task?.untimed === true && !task?.completedAt ? this.dateFormat : this.timedFormat;
  }

  /** The moment a row shows: the conclusion for a concluded activity, the schedule otherwise. */
  momentOf(task: Task): Date | undefined {
    return (task?.status === TaskStatusEnum.DONE ? task?.completedAt : undefined) ?? task?.deadlineAt;
  }

  outcomeLabel(task: Task): string {
    return TASK_OUTCOME_LABEL[task?.outcome] ?? 'kanban.taskOutcome.other';
  }

  postponementCount(task: Task): number {
    return task?.postponements?.length ?? 0;
  }

  /** The moment the activity was FIRST scheduled for (RF-26). */
  originalMoment(task: Task): Date | null {
    const first = task?.postponements?.[0];
    return first?.from ? new Date(first.from) : null;
  }

  /**
   * The row's state (RF-36): the Today reading's group when the row carries one, else derived —
   * concluded, in progress, overdue (a previous company-local day) or scheduled.
   */
  stateOf(task: Task): ActivityRowState {
    if (task?.status === TaskStatusEnum.DONE) {
      return { key: 'task.activity.stateConcluded', badge: 'is-ok', icon: 'mdi-check-circle-outline' };
    }
    switch (task?.group) {
      case 'overdue':
        return { key: 'task.activity.stateOverdue', badge: 'is-err', icon: 'mdi-alert-circle-outline' };
      case 'dueNow':
        return { key: 'task.activity.stateDueNow', badge: 'is-warn', icon: 'mdi-clock-alert-outline' };
      case 'laterToday':
        return { key: 'task.activity.stateLater', badge: 'is-run', icon: 'mdi-clock-outline' };
      case 'untimed':
        // Design (canvas "Hoje"/"Semana"): an untimed commitment is a scheduled one — the "sem
        // horário" mark lives in the Quando column, so the status badge does not repeat it.
        return { key: 'task.activity.stateScheduled', badge: 'is-neutral', icon: 'mdi-calendar-check-outline' };
    }
    if (task?.status === TaskStatusEnum.IN_PROGRESS) {
      return { key: 'task.activity.stateInProgress', badge: 'is-run', icon: 'mdi-progress-clock' };
    }
    // No date: neither scheduled nor overdue — the same mark as the Lista and the deal panels.
    if (!task?.deadlineAt) {
      return { key: 'task.activity.noDate', badge: 'is-neutral', icon: 'mdi-calendar-remove-outline' };
    }
    if (new Date(task.deadlineAt).getTime() < this.startOfToday().getTime()) {
      return { key: 'task.activity.stateOverdue', badge: 'is-err', icon: 'mdi-alert-circle-outline' };
    }
    // Design: scheduled is the neutral grey; blue (`is-run`) is kept for what is happening now.
    return { key: 'task.activity.stateScheduled', badge: 'is-neutral', icon: 'mdi-calendar-check-outline' };
  }

  onRowClick(task: Task): void {
    if (this.selectable) {
      this.selected.emit(task);
      return;
    }
    this.open(task);
  }

  open(task: Task): void {
    if (task?._id) this.router.navigate(['/task', task._id]);
  }

  openDeal(task: Task): void {
    const idCard = this.idOfDeal(task);
    if (idCard) this.router.navigate(['/kanban'], { queryParams: { card: idCard } });
  }

  /**
   * Concluir (RF-17): a deal activity opens the ONE conclusion dialog; an activity with no deal
   * completes in one step (RF-45). Either way the list says it changed.
   */
  conclude(task: Task): void {
    if (!task?._id || this.isLocked(task)) return;

    if (this.idOfDeal(task)) {
      this.dialog.open(ConcludeActivityDialogComponent, { data: { task, card: task.card }, width: '720px', disableClose: true })
        .afterClosed()
        .pipe(takeUntil(this.destroy$))
        .subscribe((result: ConcludeActivityDialogResult | undefined) => {
          if (result) this.changed.emit(result.task);
        });
      return;
    }

    this.tasksService.completeTask(task._id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (concluded) => {
          this.alertService.success(this.t('task.detail.taskCompletedSuccessfully'));
          this.changed.emit(concluded);
        },
        error: (error) => this.alertService.error(error?.error?.message ?? this.t('task.conclude.error')),
      });
  }

  /** Adiar (RF-24). */
  postpone(task: Task): void {
    if (!task?._id || this.isLocked(task)) return;

    this.dialog.open(PostponeActivityDialogComponent, { data: { task }, width: '560px' })
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((postponed: Task | undefined) => {
        if (postponed) this.changed.emit(postponed);
      });
  }

  private startOfToday(): Date {
    const day = new Date();
    day.setHours(0, 0, 0, 0);
    return day;
  }
}
